import React from "react";
import { Button, Dialog } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { withStyles } from "@mui/styles";
import Quagga from "@ericblade/quagga2";

// Глобальный флаг для отслеживания состояния камеры
let isQuaggaActive = false;
let cameraReleasePromise = Promise.resolve();

const READERS = [
	"code_128_reader",
	"code_39_reader",
	"code_39_vin_reader",
	"code_93_reader",
	"code_32_reader",
	"ean_reader",
	"ean_8_reader",
	"upc_reader",
	"upc_e_reader",
];

function hasEnvironmentFacing(device) {
	try {
		if (typeof device.getCapabilities !== "function") return false;
		const list = device.getCapabilities().facingMode;
		const arr = Array.isArray(list) ? list : list ? [list] : [];
		return arr.includes("environment");
	} catch {
		return false;
	}
}

class JsBarCodeScanner extends React.PureComponent {
	static REQUIRED_CODE_SUBSTRING = "TRC";

	state = { capturedCode: null, showCapturedFeedback: false };
	capturedFeedbackTimer = null;
	initTimeout = null;
	refRoot = React.createRef();
	onProcessedHandler = null;
	_isMounted = false;
	_isInitialized = false;
	_lastDetectedCode = null;
	_detectionDebounceTimer = null;
	_isCodeDetected = false; // Флаг для блокировки повторных обнаружений
	_scannerStopTimer = null; // Таймер для временной остановки сканера

	componentDidMount = async () => {
		this._isMounted = true;
		this._isCodeDetected = false; // Сбрасываем флаг при монтировании
		this._lastDetectedCode = null; // Сбрасываем последний обнаруженный код
		try {
			await this.initScanner();
		} catch (err) {
			console.error(err);
			this.props.onError?.(err?.message || "Не удалось запустить камеру для сканирования.");
		}
	};

	componentWillUnmount = async () => {
		this._isMounted = false;
		this._isInitialized = false;
		this._isCodeDetected = false;
		this._lastDetectedCode = null;
		
		// Очистка всех таймеров
		if (this.capturedFeedbackTimer) {
			clearTimeout(this.capturedFeedbackTimer);
			this.capturedFeedbackTimer = null;
		}
		if (this.initTimeout) {
			clearTimeout(this.initTimeout);
			this.initTimeout = null;
		}
		if (this._detectionDebounceTimer) {
			clearTimeout(this._detectionDebounceTimer);
			this._detectionDebounceTimer = null;
		}
		if (this._scannerStopTimer) {
			clearTimeout(this._scannerStopTimer);
			this._scannerStopTimer = null;
		}
		
		// Отписка от обработчиков
		if (typeof this.onProcessedHandler === "function") {
			try {
				Quagga.offProcessed(this.onProcessedHandler);
			} catch (e) {
				console.warn('[JsBarCodeScanner] Error removing onProcessed handler:', e);
			}
			this.onProcessedHandler = null;
		}
		
		// Остановка Quagga и ожидание завершения
		if (isQuaggaActive) {
			try {
				await cameraReleasePromise.catch(() => {});
				await Quagga.stop();
				isQuaggaActive = false;
				cameraReleasePromise = Promise.resolve();
			} catch (e) {
				console.warn('[JsBarCodeScanner] Error stopping Quagga:', e);
				isQuaggaActive = false;
				cameraReleasePromise = Promise.resolve();
			}
		}
		
		// Очистка DOM элементов
		if (this.refRoot.current) {
			const container = this.refRoot.current;
			const videos = container.querySelectorAll('video');
			const canvases = container.querySelectorAll('canvas');
			videos.forEach(video => {
				if (video.srcObject) {
					const tracks = video.srcObject.getTracks();
					tracks.forEach(track => {
						track.stop();
						track = null;
					});
					video.srcObject = null;
				}
				video.remove();
			});
			canvases.forEach(canvas => canvas.remove());
		}
	};

	initScanner = async () => {
		// Проверка на размонтирование
		if (!this._isMounted) return;
		
		// Проверка готовности DOM
		if (!this.refRoot.current) {
			// Используем один таймер вместо рекурсии
			if (this.initTimeout) clearTimeout(this.initTimeout);
			this.initTimeout = setTimeout(() => {
				if (this._isMounted) this.initScanner();
			}, 500);
			return;
		}
		
		// Проверка поддержки браузера
		if (!navigator?.mediaDevices?.getUserMedia) {
			this.props.onError?.("Ваш браузер не поддерживает доступ к камере.");
			return;
		}
		
		// Ожидание освобождения предыдущей камеры
		try {
			await cameraReleasePromise.catch(() => {});
		} catch (e) {
			console.warn('[JsBarCodeScanner] Camera release error:', e);
		}
		
		// Проверка на размонтирование после ожидания
		if (!this._isMounted) return;
		
		// Даем время браузеру на обновление DOM
		await new Promise((r) => requestAnimationFrame(r));
		await new Promise((r) => setTimeout(r, 200));
		
		// Финальная проверка
		if (!this._isMounted) return;
		
		const target = this.refRoot.current;
		if (!target) {
			this.props.onError?.("Контейнер сканера недоступен.");
			return;
		}
		
		// Предотвращение повторной инициализации
		if (this._isInitialized || isQuaggaActive) {
			console.warn('[JsBarCodeScanner] Scanner already initialized, skipping...');
			return;
		}

		// Получение доступных камер
		let videoInputs = [];
		try {
			videoInputs = (await navigator.mediaDevices.enumerateDevices()).filter(
				(d) => d.kind === "videoinput"
			);
		} catch (e) {
			console.warn('[JsBarCodeScanner] Error enumerating devices:', e);
		}
		
		const devices = videoInputs.filter(hasEnvironmentFacing).sort((a, b) => {
			try {
				const aMax = a.getCapabilities?.()?.frameRate?.max ?? 0;
				const bMax = b.getCapabilities?.()?.frameRate?.max ?? 0;
				return bMax - aMax;
			} catch {
				return 0;
			}
		});
		const selectedDevice = devices[0] || null;
		
		// Оптимизированные ограничения для слабых устройств
		// Используем более низкое разрешение для лучшей производительности
		let widthConstraint = { ideal: 1280, max: 1920, min: 640 };
		let heightConstraint = { ideal: 720, max: 1080, min: 480 };
		
		try {
			if (selectedDevice?.getCapabilities) {
				const cap = selectedDevice.getCapabilities();
				if (cap.width?.max != null && cap.height?.max != null) {
					// Ограничиваем максимальное разрешение для производительности
					const maxWidth = Math.min(cap.width.max, 1280);
					const maxHeight = Math.min(cap.height.max, 720);
					widthConstraint = { ideal: maxWidth, max: maxWidth, min: 640 };
					heightConstraint = { ideal: maxHeight, max: maxHeight, min: 480 };
				}
			}
		} catch (_) {}

		// Проверка на размонтирование перед инициализацией
		if (!this._isMounted) return;
		
		Quagga.init(
			{
				// Снижаем frequency для лучшей производительности на слабых устройствах
				frequency: 10,
				inputStream: {
					type: "LiveStream",
					target,
					constraints: {
						facingMode: { ideal: "environment" },
						width: widthConstraint,
						height: heightConstraint,
						// Оптимизация для производительности
						frameRate: { ideal: 15, max: 30 },
						...(selectedDevice?.deviceId ? { deviceId: { exact: selectedDevice.deviceId } } : {}),
					},
					area: { top: "0%", right: "0%", left: "0%", bottom: "0%" },
				},
				locator: { patchSize: "x-small", halfSample: true },
				decoder: { readers: READERS, multiple: false, locate: true },
				locate: false,
			},
			(err) => {
				if (!this._isMounted) return;
				
				if (err) {
					console.error('[JsBarCodeScanner] Init error:', err);
					this._isInitialized = false;
					isQuaggaActive = false;
					const isPermissionDenied =
						err?.name === "NotAllowedError" ||
						err?.name === "PermissionDeniedError" ||
						/permission|denied|разрешени/i.test(err?.message || "");
					this.props.onError?.(
						isPermissionDenied
							? "Доступ к камере запрещён. Разрешите использование камеры в настройках браузера или сайта."
							: err?.message || "Не удалось запустить камеру для сканирования."
					);
					return;
				}
				
				if (!this._isMounted) {
					Quagga.stop().catch(() => {});
					return;
				}
				
				try {
					Quagga.start();
					this._isInitialized = true;
					isQuaggaActive = true;
				} catch (startErr) {
					console.error('[JsBarCodeScanner] Start error:', startErr);
					this._isInitialized = false;
					isQuaggaActive = false;
					this.props.onError?.("Не удалось запустить сканер.");
				}
			}
		);

		this.onProcessedHandler = (result) => {
			try {
				if (!this._isMounted || !this._isInitialized || this._isCodeDetected) return;
				
				const ctx = Quagga.canvas?.ctx?.overlay;
				const canvas = Quagga.canvas?.dom?.overlay;
				if (!ctx || !canvas || !document.contains(canvas)) return;
				
				const w = parseInt(canvas.getAttribute("width"), 10) || 0;
				const h = parseInt(canvas.getAttribute("height"), 10) || 0;
				if (w <= 0 || h <= 0) return;
				
				// Очистка canvas
				ctx.clearRect(0, 0, w, h);
				
				if (result) {
					// Рисование боксов только если есть результат и код еще не обнаружен
					if (result.boxes && result.boxes.length > 0) {
						result.boxes
							.filter((box) => box !== result.box)
							.forEach((box) =>
								Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, ctx, { color: "green", lineWidth: 2 })
							);
					}
					if (result.box) {
						Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, ctx, { color: "#00F", lineWidth: 2 });
					}
					
					// Обработка обнаруженного кода с debounce (только если код еще не обнаружен)
					if (result.codeResult?.code && this._isMounted && !this._isCodeDetected) {
						this.onDetected(result);
					}
				}
			} catch (e) {
				console.warn('[JsBarCodeScanner] onProcessedHandler error:', e);
			}
		};
		
		try {
			Quagga.onProcessed(this.onProcessedHandler);
		} catch (e) {
			console.error('[JsBarCodeScanner] Error setting onProcessed handler:', e);
			this.props.onError?.("Ошибка инициализации обработчика сканера.");
		}
	};

	onDetected = (event) => {
		try {
			if (!this._isMounted || !this._isInitialized) return;
			
			// Блокируем обработку если код уже был обнаружен и сканер временно остановлен
			if (this._isCodeDetected) return;
			
			const codeStr = String(event?.codeResult?.code ?? "").trim();
			if (!codeStr) return;
			
			// Проверка на требуемый подстроку
			if (!codeStr.toUpperCase().includes(JsBarCodeScanner.REQUIRED_CODE_SUBSTRING.toUpperCase())) return;
			
			// Debounce: игнорируем повторные обнаружения того же кода
			if (this._lastDetectedCode === codeStr) return;
			
			// Очистка предыдущего debounce таймера
			if (this._detectionDebounceTimer) {
				clearTimeout(this._detectionDebounceTimer);
				this._detectionDebounceTimer = null;
			}
			
			// Debounce для предотвращения множественных вызовов
			this._detectionDebounceTimer = setTimeout(async () => {
				if (!this._isMounted || this._isCodeDetected) return;
				
				// Устанавливаем флаг блокировки сразу, чтобы предотвратить повторные обнаружения
				this._isCodeDetected = true;
				this._lastDetectedCode = codeStr;
				
				// Временно останавливаем сканер, чтобы предотвратить повторные обнаружения
				try {
					if (isQuaggaActive) {
						await Quagga.stop();
						isQuaggaActive = false;
					}
				} catch (e) {
					console.warn('[JsBarCodeScanner] Error stopping scanner after detection:', e);
				}
				
				// Очистка предыдущего таймера обратной связи
				if (this.capturedFeedbackTimer) {
					clearTimeout(this.capturedFeedbackTimer);
				}
				
				// Обновление состояния
				this.setState({ capturedCode: codeStr, showCapturedFeedback: true });
				
				// Вызов колбэков
				this.props.onResult?.(codeStr);
				this.props.onSuccess?.(codeStr);
				
				// Автоматическое скрытие обратной связи через 2 секунды
				this.capturedFeedbackTimer = setTimeout(() => {
					if (this._isMounted) {
						this.setState({ showCapturedFeedback: false });
					}
					this.capturedFeedbackTimer = null;
				}, 2000);
				
				this._detectionDebounceTimer = null;
			}, 300); // Debounce 300ms
		} catch (e) {
			console.warn('[JsBarCodeScanner] onDetected error:', e);
		}
	};

	render() {
		const { classes, onCancel } = this.props;
		const { showCapturedFeedback, capturedCode } = this.state;
		return (
			<Dialog
				open
				fullScreen
				PaperProps={{
					className: classes.dialogPaper,
					style: { background: "#000", margin: 0, maxHeight: "100vh", overflow: "hidden" },
				}}
			>
				<div className={classes.wrapper}>
					<div ref={this.refRoot} id="JsBarCodeScanner" className={classes.root} />
					{showCapturedFeedback && (
						<div className={classes.capturedOverlay}>
							<CheckCircle className={classes.capturedIcon} />
							<span className={classes.capturedTitle}>Штрих-код считан!</span>
							<span className={classes.capturedCode}>{capturedCode}</span>
						</div>
					)}
					<div className={classes.hint}>Наведите камеру на штрих-код</div>
					<Button className={classes.buttonClose} variant="contained" color="primary" onClick={onCancel}>
						Отменить
					</Button>
				</div>
			</Dialog>
		);
	}
}

const styles = {
	dialogPaper: {
		overflow: "hidden",
		maxHeight: "100vh",
		"@supports (height: 100dvh)": { maxHeight: "100dvh" },
		"& .MuiDialog-paper": { margin: 0, maxHeight: "100%" },
	},
	wrapper: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		width: "100vw",
		height: "100vh",
		minHeight: "-webkit-fill-available",
		"@supports (height: 100dvh)": { height: "100dvh" },
		display: "flex",
		flexDirection: "column",
		background: "#000",
		zIndex: 1,
	},
	root: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		width: "100%",
		height: "100%",
		overflow: "hidden",
		"& video": {
			position: "absolute !important",
			top: "0 !important",
			left: "0 !important",
			width: "100% !important",
			height: "100% !important",
			objectFit: "contain",
			display: "block",
		},
		"& canvas": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100% !important",
			height: "100% !important",
			maxWidth: "100%",
			maxHeight: "100%",
			objectFit: "contain",
			zIndex: 10,
			pointerEvents: "none",
		},
	},
	capturedOverlay: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: "24px 32px",
		background: "rgba(0, 180, 80, 0.95)",
		borderRadius: 16,
		boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
		zIndex: 150,
		pointerEvents: "none",
		animation: "$capturedPulse 0.4s ease-out",
	},
	"@keyframes capturedPulse": {
		"0%": { transform: "translate(-50%, -50%) scale(0.85)", opacity: 0 },
		"70%": { transform: "translate(-50%, -50%) scale(1.05)", opacity: 1 },
		"100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
	},
	capturedIcon: { fontSize: 56, color: "#fff", marginBottom: 8 },
	capturedTitle: { color: "#fff", fontSize: 18, fontWeight: 600, marginBottom: 4 },
	capturedCode: {
		color: "rgba(255,255,255,0.95)",
		fontSize: 16,
		fontFamily: "monospace",
		wordBreak: "break-all",
		textAlign: "center",
		maxWidth: "80vw",
	},
	hint: {
		position: "absolute",
		top: 24,
		left: "50%",
		transform: "translateX(-50%)",
		padding: "8px 16px",
		background: "rgba(0,0,0,0.6)",
		color: "#fff",
		borderRadius: 8,
		fontSize: 14,
		zIndex: 100,
		pointerEvents: "none",
	},
	buttonClose: {
		position: "absolute",
		bottom: "calc(72px + env(safe-area-inset-bottom, 0px))",
		left: 12,
		right: 12,
		zIndex: 200,
		"@supports (height: 1dvh)": { bottom: "calc(12px + 10dvh + env(safe-area-inset-bottom, 0px))" },
	},
};

export default withStyles(styles)(JsBarCodeScanner);
